/**
 * React Router 7 Root Layout
 * Integrates Shopify App Bridge and Polaris for embedded app experience
 * 
 * Structure follows React Router 7 conventions:
 * - Layout: HTML document shell (shared by App, ErrorBoundary, HydrateFallback)
 * - App: Main application content
 * - HydrateFallback: Loading state during hydration
 * - ErrorBoundary: Error handling UI
 */

import { Links, Meta, Outlet, Scripts, ScrollRestoration, useLoaderData, useLocation } from 'react-router';
import { useEffect, useState, type ReactNode } from 'react';
import type { LoaderFunctionArgs, MetaFunction } from 'react-router';
import { AppProvider, Frame } from '@shopify/polaris';
import type { Env } from '../src/types';

// ClientPolarisVizProvider is in a shared module for reuse across layouts
import { ClientPolarisVizProvider } from './components/providers/ClientPolarisVizProvider';
import { ToastProvider, ToastRenderer } from './components/feedback/ToastProvider';
import { NavigationProgress } from './components/feedback/NavigationProgress';
import { NetworkStatus } from './components/feedback/NetworkStatus';
import { AppNav } from './components/app-bridge/AppNav';
import { SupportHandler } from './components/app-bridge/SupportHandler';
import { ClientErrorHandler } from './components/core/ClientErrorHandler';
import { reportError } from './utils/error-reporter';
import { setShopContext, captureException as sentryCaptureException } from './utils/sentry.client';
import { getEnv } from '~/utils/cloudflare-context';
import '@shopify/polaris/build/esm/styles.css';
import '@shopify/polaris-viz/build/esm/styles.css';
import './styles/index.css';

// Type for loader data
interface RootLoaderData {
  shopifyApiKey: string;
  host: string;
  environment: string;
  showNav: boolean;
  shopDomain?: string;
}

// PERFORMANCE: Memoize empty i18n object to prevent AppProvider re-renders
const EMPTY_I18N = {};

/**
 * Meta function for root route
 *
 * Provides the shopify-api-key meta tag required for App Bridge.
 * Using the meta function (instead of rendering in Layout) ensures
 * the value is consistent between SSR and client hydration, avoiding
 * Firefox hydration mismatches.
 */
export const meta: MetaFunction<typeof loader> = ({ data }) => {
  const metaTags = [];

  // Add shopify-api-key meta tag if available
  // This is required by Shopify App Bridge for initialization
  if (data?.shopifyApiKey) {
    metaTags.push({
      name: 'shopify-api-key',
      content: data.shopifyApiKey,
    });
  }

  return metaTags;
};

/**
 * Layout Component - HTML Document Shell
 *
 * This is shared between App, ErrorBoundary, and HydrateFallback.
 * App Bridge script and meta tag are included server-side to ensure
 * proper initialization before React hydration.
 *
 * CRITICAL: Per Shopify requirements, App Bridge must be:
 * 1. The first <script> tag in the document
 * 2. Loaded synchronously (no async/defer)
 * 3. Preceded by the shopify-api-key meta tag
 */
export function Layout({ children }: { children: React.ReactNode }) {
  // CRITICAL: Do NOT use hooks like useRouteLoaderData in Layout for head content.
  // Layout is shared between App, ErrorBoundary, and HydrateFallback, and hooks
  // may return different values during SSR vs client hydration, causing mismatches.
  //
  // The shopify-api-key meta tag is provided via the `meta` function export,
  // which ensures consistent values between SSR and client hydration.

  return (
    <html lang="en">
      <head>
        <meta charSet="utf-8" />
        <meta name="viewport" content="width=device-width, initial-scale=1" />
        <Links />
        <Meta />
        {/* App Bridge script - must come after Meta (which renders shopify-api-key) */}
        <script src="https://cdn.shopify.com/shopifycloud/app-bridge.js" />
        <link rel="preconnect" href="https://fonts.googleapis.com" />
        <link rel="preconnect" href="https://fonts.gstatic.com" crossOrigin="anonymous" />
        <link href="https://fonts.googleapis.com/css2?family=Inter:wght@300;400;500;600;700;800;900&display=swap" rel="stylesheet" />
      </head>
      <body>
        {children}
        <ScrollRestoration />
        <Scripts />
      </body>
    </html>
  );
}

/**
 * HydrateFallback - Loading state during hydration
 * 
 * This is rendered inside Layout during the hydration phase.
 * It should be a simple, static UI that doesn't require any client-side logic.
 */
export function HydrateFallback() {
  return (
    <div 
      className="hydration-fallback"
      style={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        backgroundColor: '#f6f6f7',
        zIndex: 9999,
      }}
    >
      <div style={{
        width: '24px',
        height: '24px',
        minWidth: '24px',
        minHeight: '24px',
        flexShrink: 0,
        border: '2px solid #e1e3e5',
        borderTopColor: '#008060',
        borderRadius: '50%',
        boxSizing: 'border-box',
        animation: 'spin 0.8s linear infinite',
      }} />
      <style>{`
        @keyframes spin {
          to { transform: rotate(360deg); }
        }
      `}</style>
    </div>
  );
}

/**
 * App Component - Main Application
 *
 * This renders inside the Layout component after hydration completes.
 * App Bridge is now loaded server-side in the Layout component's <head>
 * to satisfy Shopify's requirement that it be the first script tag.
 */
export default function App() {
  const { shopDomain, showNav } = useLoaderData<typeof loader>();
  const location = useLocation();

  // Check if we're in a modal route - modals should NOT render Frame
  // Frame adds navigation links (Dashboard, Settings, Help) that shouldn't appear in modals
  // Match both /app/modal/* and /app/settings/modal/* routes
  const isModalRoute = location.pathname.startsWith('/app/modal') ||
    location.pathname.includes('/modal/');

  // Set Sentry user context by shop domain
  useEffect(() => {
    if (shopDomain) {
      setShopContext(shopDomain);
    }
  }, [shopDomain]);

  // For modal routes, render minimal wrapper without Frame/navigation
  if (isModalRoute) {
    return (
      <div className="app-shell content-fade-in">
        <ClientErrorHandler />
        <div className="app-shell-content">
          <AppProvider i18n={EMPTY_I18N}>
            <ClientPolarisVizProvider>
              <ToastProvider>
                <Outlet />
                <ToastRenderer />
              </ToastProvider>
            </ClientPolarisVizProvider>
          </AppProvider>
        </div>
      </div>
    );
  }

  return (
    <div className="app-shell content-fade-in">
      <ClientErrorHandler />
      <div className="app-shell-content">
        <SupportHandler />
        <AppNav
          items={showNav ? [
            { label: 'Dashboard', href: '/app' },
            { label: 'Settings', href: '/app/settings' },
            { label: 'Help', href: '/app/help' },
          ] : []}
        />
        <AppProvider i18n={EMPTY_I18N}>
          <ClientPolarisVizProvider>
            <Frame>
              <ToastProvider>
                <NetworkStatus />
                <NavigationProgress />
                <Outlet />
                <ToastRenderer />
              </ToastProvider>
            </Frame>
          </ClientPolarisVizProvider>
        </AppProvider>
      </div>
    </div>
  );
}

/**
 * Loader: Provides Shopify API key and host for App Bridge initialization
 * 
 * Development Mode:
 * When DEV_SHOP is set in wrangler.toml vars, the app will use mock values
 * for standalone development without Shopify. This allows UI development
 * with `npm run dev` (wrangler dev only) without needing shopify app dev.
 * 
 * For full Shopify integration testing, use `npm run dev:full` which runs
 * wrangler dev + shopify app dev together.
 */
export async function loader({ request, context }: LoaderFunctionArgs) {
  const env = getEnv(context);
  const url = new URL(request.url);

  // Detect environment from env variable first, then fallback to hostname detection
  // This allows wrangler.dev.toml's ENVIRONMENT variable to override hostname-based detection
  const hostname = url.hostname;
  const envEnvironment = env.ENVIRONMENT;
  const environment = envEnvironment
    ? envEnvironment
    : hostname.includes('staging')
      ? 'staging'
      : hostname.includes('localhost') || hostname.includes('127.0.0.1') || hostname.includes('trycloudflare.com')
        ? 'development'
        : 'production';

  // Development mode: Allow standalone development without Shopify
  // Check for DEV_SHOP env var to enable mock mode
  const devShop = env.DEV_SHOP;
  const isDevMode = environment === 'development' && devShop;
  
  // Get host parameter from Shopify (base64-encoded value for App Bridge)
  // In dev mode, generate a mock host if not provided
  let host = url.searchParams.get('host') || '';
  
  if (!host && isDevMode) {
    // Generate mock host for development (base64 encoded shop domain)
    host = btoa(`${devShop}/admin`);
  }

  // Dev mode logging removed - environment can be inferred from deployment

  if (!host && !isDevMode) {
    // No host parameter in production - App Bridge initialization may fail
  }

  // Check if shop exists and onboarding is complete to determine if we should show navigation
  let showNav = true;
  // In dev mode, use DEV_SHOP if shop param is not provided
  const shop = url.searchParams.get('shop') || url.searchParams.get('embedded') || (isDevMode ? devShop : null);

  if (shop) {
    try {
      // Import getShopByDomain from shop-lookup (separate file to avoid @sentry/cloudflare bundling)
      const { getShopByDomain } = await import('../src/auth/shop-lookup');
      const shopRecord = await getShopByDomain(shop, env.DB);

      // Hide navigation if shop not found
      if (!shopRecord) {
        showNav = false;
      } else {
        // Check if onboarding is complete
        const onboardingProgress = await env.DB
          .prepare('SELECT onboarding_completed FROM onboarding_progress WHERE shop_id = ?')
          .bind(shopRecord.id)
          .first() as { onboarding_completed: number } | null;

        // Hide navigation if onboarding not complete
        const isOnboardingComplete = onboardingProgress?.onboarding_completed === 1;

        if (!isOnboardingComplete) {
          showNav = false;
        }
      }
    } catch (error) {
      // Error checking shop/onboarding - show navigation to avoid breaking the app
    }
  }

  return {
    shopifyApiKey: env.SHOPIFY_API_KEY,
    host: host,
    environment: environment,
    showNav: showNav,
    shopDomain: shop || undefined,
  };
}

/**
 * Error Boundary: Graceful error handling for the entire app
 *
 * This is rendered inside the Layout component, so it should NOT
 * render its own HTML document structure.
 */
export function ErrorBoundary({ error }: { error?: Error }) {
  // Report error to BOTH Sentry and custom error-reporter (redundancy)
  if (typeof window !== 'undefined' && error) {
    // Sentry capture (primary)
    sentryCaptureException(error, {
      component: 'root',
      type: 'error-boundary',
    });

    // Custom error reporter (redundancy - existing)
    reportError(error, {
      component: 'root',
      type: 'boundary-error',
    });
  }

  return (
    <>
      <style>{`
        .error-page-wrapper {
          font-family: Inter, -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif;
          background-color: #f6f6f7;
          min-height: 100vh;
          display: flex;
          align-items: center;
          justify-content: center;
          padding: 20px;
        }
        .error-container {
          max-width: 480px;
          text-align: center;
          background: white;
          border-radius: 12px;
          padding: 40px;
          box-shadow: 0 1px 3px rgba(0,0,0,0.1);
        }
        .error-icon {
          width: 64px;
          height: 64px;
          margin: 0 auto 20px;
          background: #FFF4E5;
          border-radius: 50%;
          display: flex;
          align-items: center;
          justify-content: center;
        }
        .error-title {
          font-size: 20px;
          font-weight: 600;
          color: #1a1a1a;
          margin-bottom: 8px;
        }
        .error-message {
          font-size: 14px;
          color: #6d7175;
          margin-bottom: 24px;
          line-height: 1.5;
        }
        .error-actions {
          display: flex;
          gap: 12px;
          justify-content: center;
          flex-wrap: wrap;
        }
        .btn {
          padding: 10px 20px;
          border-radius: 6px;
          font-size: 14px;
          font-weight: 500;
          text-decoration: none;
          cursor: pointer;
          transition: all 0.2s;
        }
        .btn-primary {
          background: #008060;
          color: white;
          border: none;
        }
        .btn-primary:hover {
          background: #006e52;
        }
        .btn-secondary {
          background: white;
          color: #1a1a1a;
          border: 1px solid #c9cccf;
        }
        .btn-secondary:hover {
          background: #f6f6f7;
        }
      `}</style>
      <div className="error-page-wrapper">
        <div className="error-container">
          <div className="error-icon">
            <svg width="32" height="32" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path d="M10 6V10M10 14H10.01M19 10C19 14.9706 14.9706 19 10 19C5.02944 19 1 14.9706 1 10C1 5.02944 5.02944 1 10 1C14.9706 1 19 5.02944 19 10Z" stroke="#B98900" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
            </svg>
          </div>
          <h1 className="error-title">Something went wrong</h1>
          <p className="error-message">
            We encountered an unexpected error. Please try refreshing the page
            or return to the Shopify admin to restart the app.
          </p>
          <div className="error-actions">
            <button
              className="btn btn-primary"
              onClick={() => typeof window !== 'undefined' && window.location.reload()}
            >
              Refresh Page
            </button>
            <a
              href="https://admin.shopify.com"
              className="btn btn-secondary"
            >
              Go to Shopify Admin
            </a>
          </div>
        </div>
      </div>
    </>
  );
}
